import type React from 'react';
import { useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';

import CalendarIconSvg from 'app/assets/training-plans/training-plan-tracker/calendar-icon.svg';

import type { ProgressivePlanInstance } from 'app/types/graphql';
import Typography from 'app/ui/components/atoms/typography';
import { routes } from 'app/routes/constants';
import { trackEvent } from 'src/app/utils/analytics/amplitude/tracker';
import { PlanTrackingLocation } from 'src/app/utils/analytics/amplitude/types';
import {
  WorkoutDayStatus,
  WeekStatus,
  type TimeStatus,
  getCircleSvg,
  getMoonIconSvg,
  getRestDayCircleSvg,
  getWeekStatus,
} from 'src/app/ui/components/molecules/training-plan-instance-tracker/utils';

const DEFAULT_DESKTOP_IMAGE = '/training-plan-tracker/trackerimg1desktop.png';
const DEFAULT_MOBILE_IMAGE = '/training-plan-tracker/trackerimg1mobile.png';

export type TrackerVariant = 'small' | 'medium' | 'large';

export type TrainingPlanInstanceTrackerProps = {
  planInstance: ProgressivePlanInstance;
  className?: string;
  variant?: TrackerVariant;
};

const Container = styled.div<{ $variant: TrackerVariant }>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  min-height: 10.375rem;
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.faqGrey};
  border: 1px solid ${({ theme }) => theme.colors.veryGray};

  ${({ theme: { media }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    min-height: 15rem;
  `,
    )}

  ${({ theme: { media }, $variant }) =>
    $variant === 'large' &&
    media.large(
      () => `
    min-height: 18.75rem;
  `,
    )}
`;

const CardInner = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  flex: 1;
`;

const ImageBackground = styled.div<{ $mobileImage: string; $desktopImage: string; $variant: TrackerVariant }>`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 50%;
  background-size: auto 100%;
  background-position: right bottom;
  background-repeat: no-repeat;
  background-image: url("${({ $mobileImage }) => $mobileImage}");

  ${({ theme: { media }, $desktopImage, $variant }) =>
    $variant === 'large' &&
    media.large(
      () => `
    background-image: url("${$desktopImage}");
    background-size: cover;
    background-position: center right;
  `,
    )}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(
      to right,
      ${({ theme }) => theme.colors.faqGrey} 0%,
      transparent 50%
    );
  }
`;

const ContentWrapper = styled.div<{ $variant: TrackerVariant }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)}`};
  gap: ${({ theme }) => theme.spacing(4)};

  ${({ theme: { media, spacing }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    padding: ${spacing(4)} ${spacing(6)};
    width: 60%;
  `,
    )}

  ${({ theme: { media, spacing }, $variant }) =>
    $variant === 'large' &&
    media.large(
      () => `
    padding: ${spacing(6)};
    width: 55%;
  `,
    )}
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const PlanName = styled(Typography)<{ $variant: TrackerVariant }>`
  display: none;
  margin: 0;

  ${({ theme: { media }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    display: block;
  `,
    )}
`;

const WeekTitle = styled.p<{ $variant: TrackerVariant }>`
  font-family: ${({ theme }) => theme.typography.poppins};
  font-weight: 600;
  font-size: 1.375rem;
  line-height: 1.625rem;
  color: ${({ theme }) => theme.colors.white};
  letter-spacing: 0.007rem;
  margin-bottom: ${({ theme }) => theme.spacing(2)};

  ${({ theme: { media }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    font-size: 2.25rem;
    line-height: 2.625rem;
    letter-spacing: 0.011rem;
  `,
    )}
`;

const WeeklyProgress = styled.div<{ $variant: TrackerVariant }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  align-items: center;

  ${({ theme: { media, spacing }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    gap: ${spacing(4)};
  `,
    )}
`;

const DayCircle = styled.div<{ $variant: TrackerVariant }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  flex-shrink: 0;
  position: relative;

  ${({ theme: { media }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    width: 2.125rem;
    height: 2.125rem;
  `,
    )}

  ${({ theme: { media }, $variant }) =>
    $variant === 'large' &&
    media.large(
      () => `
    width: 2.5rem;
    height: 2.5rem;
  `,
    )}
`;

const CircleBackground = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const DayNumber = styled.span<{ $status: WorkoutDayStatus; $variant: TrackerVariant }>`
  position: relative;
  z-index: 1;
  font-family: ${({ theme }) => theme.typography.poppins};
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.375rem;
  letter-spacing: 0.0225rem;
  text-align: center;

  ${({ theme: { media }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    font-size: 1.25rem;
    line-height: 1.625rem;
    letter-spacing: 0.0375rem;
  `,
    )}

  color: ${({ $status, theme }) => {
    switch ($status) {
      case WorkoutDayStatus.COMPLETED:
        return theme.colors.black;
      case WorkoutDayStatus.TODAY:
        return theme.colors.focoltone;
      default:
        return theme.colors.knop;
    }
  }};
`;

const MoonIcon = styled.img<{ $variant: TrackerVariant }>`
  position: relative;
  z-index: 1;
  width: 0.625rem;
  height: 0.625rem;

  ${({ theme: { media }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    width: 1rem;
    height: 1rem;
  `,
    )}

  ${({ theme: { media }, $variant }) =>
    $variant === 'large' &&
    media.large(
      () => `
    width: 1.1875rem;
    height: 1.1875rem;
  `,
    )}
`;

const ProgressBarSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const WeekLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const CalendarIcon = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WeekText = styled.span<{ $variant: TrackerVariant }>`
  font-family: ${({ theme }) => theme.typography.poppins};
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.5rem;
  color: ${({ theme }) => theme.colors.white};
  letter-spacing: 0.002rem;

  ${({ theme: { media }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    font-size: 1.25rem;
    line-height: 1.625rem;
    letter-spacing: 0.0375rem;
  `,
    )}
`;

const BlocksProgressBar = styled.div<{ $variant: TrackerVariant }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 0.375rem;
  width: 100%;
  max-width: 13rem;

  ${({ theme: { media }, $variant }) =>
    $variant !== 'small' &&
    media.medium(
      () => `
    max-width: none;
    width: 100%;
  `,
    )}
`;

const BlockGroup = styled.div`
  display: flex;
  flex: 1;
  gap: 0.125rem;
`;

const WeekSegment = styled.div<{
  $isCompleted: boolean;
  $isCurrent: boolean;
  $isFirst: boolean;
  $isLast: boolean;
  $isOnlyOne: boolean;
}>`
  flex: 1;
  height: 0.375rem;
  background: ${({ $isCompleted, $isCurrent, theme }) => {
    if ($isCompleted) {
      return theme.colors.paragraphGray;
    }
    if ($isCurrent) {
      return theme.colors.focoltone;
    }
    return theme.colors.blackSwan;
  }};

  ${({ $isFirst, $isOnlyOne }) =>
    ($isFirst || $isOnlyOne) &&
    css`
      border-top-left-radius: 0.25rem;
      border-bottom-left-radius: 0.25rem;
    `}

  ${({ $isLast, $isOnlyOne }) =>
    ($isLast || $isOnlyOne) &&
    css`
      border-top-right-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
    `}
`;

const TrainingPlanInstanceTracker: React.FC<TrainingPlanInstanceTrackerProps> = ({
  planInstance,
  className,
  variant = 'large',
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();

  const { currentBlock, currentWeek, todaysWorkout, progressivePlan } = planInstance;

  const getLocationFromPath = () => {
    if (pathname === '/') {
      return PlanTrackingLocation.HOMESCREEN;
    }
    if (pathname.startsWith('/library')) {
      return PlanTrackingLocation.LIBRARY;
    }
    return PlanTrackingLocation.UNKNOWN;
  };

  const handleClick = () => {
    trackEvent('active_plan_card_clicked', {
      plan_instance_id: planInstance.id,
      plan_title: progressivePlan.name,
      location: getLocationFromPath(),
    });

    navigate(routes.TRAINING_PLAN_ACTIVE_INSTANCE.replace(':id', planInstance.id));
  };

  const currentWeekWorkouts = currentWeek?.workouts || [];

  const { blocks, totalWeeks } = useMemo(() => {
    const blocksData = progressivePlan.blocks || [];
    const total = blocksData.reduce((sum, block) => sum + (block.weeks?.length || 0), 0);
    return { blocks: blocksData, totalWeeks: total };
  }, [progressivePlan.blocks]);

  const currentWeekNumber = currentWeek?.weekNumber || 1;

  const blockMobileImageUrl =
    currentBlock?.mobileImage?.signedUrl || currentBlock?.mobileImage?.url || DEFAULT_MOBILE_IMAGE;
  const blockDesktopImageUrl =
    currentBlock?.desktopImage?.signedUrl || currentBlock?.desktopImage?.url || DEFAULT_DESKTOP_IMAGE;

  const todaysDayOfWeek = todaysWorkout?.progressivePlanWorkout.dayOfWeek ?? 0;
  const weekSchedule = useMemo(() => {
    return currentWeekWorkouts.map((workout) => {
      const lessonId = workout.lesson?.id ?? null;
      const workoutDayOfWeek = workout.dayOfWeek;
      const isRestDay = lessonId === null;

      let timeStatus: TimeStatus = WorkoutDayStatus.UPCOMING;
      if (workoutDayOfWeek < todaysDayOfWeek) {
        timeStatus = WorkoutDayStatus.COMPLETED;
      } else if (workoutDayOfWeek === todaysDayOfWeek) {
        timeStatus = WorkoutDayStatus.TODAY;
      }

      const status: WorkoutDayStatus = isRestDay ? WorkoutDayStatus.REST : timeStatus;

      return {
        dayNumber: workoutDayOfWeek,
        status,
        isRestDay,
        timeStatus,
      };
    });
  }, [currentWeekWorkouts, todaysDayOfWeek]);

  const blockTitleFormatted = `${currentBlock?.name} ${intl.formatMessage({ id: 'trainingPlan.week' })} ${currentWeekNumber}: ${intl.formatMessage({ id: 'trainingPlan.day' })} ${todaysWorkout?.progressivePlanWorkout.dayOfWeek || 1}`;

  const progressBarData = useMemo(() => {
    let runningWeekNumber = 0;
    return blocks.map((block) => ({
      id: block.id,
      weeks: (block.weeks || []).map((week, weekIndex, arr) => {
        runningWeekNumber++;
        return {
          id: week.id,
          weekStatus: getWeekStatus(runningWeekNumber, currentWeekNumber),
          isFirst: weekIndex === 0,
          isLast: weekIndex === arr.length - 1,
          isOnlyOne: arr.length === 1,
        };
      }),
    }));
  }, [blocks, currentWeekNumber]);

  return (
    <Container className={className} onClick={handleClick} $variant={variant}>
      <CardInner>
        <ImageBackground $mobileImage={blockMobileImageUrl} $desktopImage={blockDesktopImageUrl} $variant={variant} />
        <ContentWrapper $variant={variant}>
          <TitleSection>
            <PlanName variant="body1" color={theme.colors.lightGray} $variant={variant}>
              {progressivePlan.name}
            </PlanName>
            <WeekTitle $variant={variant}>{blockTitleFormatted}</WeekTitle>
            <WeeklyProgress $variant={variant}>
              {weekSchedule.map(({ dayNumber, status, isRestDay, timeStatus }) => (
                <DayCircle key={dayNumber} $variant={variant}>
                  <CircleBackground
                    src={isRestDay ? getRestDayCircleSvg(timeStatus) : getCircleSvg(status)}
                    aria-hidden="true"
                  />
                  {isRestDay ? (
                    <MoonIcon src={getMoonIconSvg(timeStatus)} alt="Rest day" $variant={variant} />
                  ) : status === WorkoutDayStatus.COMPLETED ? null : (
                    <DayNumber $status={status} $variant={variant}>
                      {dayNumber}
                    </DayNumber>
                  )}
                </DayCircle>
              ))}
            </WeeklyProgress>
          </TitleSection>

          <ProgressBarSection>
            <WeekLabel>
              <CalendarIcon>
                <img src={CalendarIconSvg} alt="Calendar icon" width={20} height={20} aria-hidden="true" />
              </CalendarIcon>
              <WeekText $variant={variant}>
                {intl.formatMessage({ id: 'trainingPlan.week' }).toUpperCase()} {currentWeekNumber}/{totalWeeks}
              </WeekText>
            </WeekLabel>
            <BlocksProgressBar $variant={variant}>
              {progressBarData.map((block) => (
                <BlockGroup key={block.id}>
                  {block.weeks.map((week) => (
                    <WeekSegment
                      key={week.id}
                      $isCompleted={week.weekStatus === WeekStatus.COMPLETED}
                      $isCurrent={week.weekStatus === WeekStatus.CURRENT}
                      $isFirst={week.isFirst}
                      $isLast={week.isLast}
                      $isOnlyOne={week.isOnlyOne}
                    />
                  ))}
                </BlockGroup>
              ))}
            </BlocksProgressBar>
          </ProgressBarSection>
        </ContentWrapper>
      </CardInner>
    </Container>
  );
};

export default TrainingPlanInstanceTracker;
